import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppNav from '../../components/app/AppNav';
import ImageUploader from '../../components/app/ImageUploader';
import { MARKETPLACE_CATEGORIES, MARKETPLACE_EMOJI } from '../../constants/marketplace';
import { apiGet, apiPost, apiPut, apiDelete } from '../../api/api';
import AIDescriptionHelper from '../../components/app/AIDescriptionHelper';
import LocationInput from '../../components/app/LocationInput';
import { slugify } from '../../utils/slug';

type Visibility = 'community' | 'global' | 'private';
type ListingType = 'Sell' | 'Rent' | 'Free';
type ItemCondition = 'New' | 'Like New' | 'Good' | 'Fair' | 'Poor' | 'Not Applicable';
type Currency = 'USD' | 'EUR' | 'GBP' | 'INR' | 'CAD' | 'AUD';

const LISTING_TYPES: ListingType[] = ['Sell', 'Rent', 'Free'];
const ITEM_CONDITIONS: ItemCondition[] = ['New', 'Like New', 'Good', 'Fair', 'Poor', 'Not Applicable'];
const CURRENCIES: Currency[] = ['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD'];
const CURRENCY_SYMBOL: Record<Currency, string> = { USD: '$', EUR: '€', GBP: '£', INR: '₹', CAD: 'C$', AUD: 'A$' };

const inputStyle: React.CSSProperties = {
  width: '100%', backgroundColor: '#f7f8fc', border: '1.5px solid #e2e6f0', borderRadius: '10px',
  padding: '0.875rem', color: '#1a1f36', fontSize: '0.9rem', outline: 'none',
  boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.15s',
};
const labelStyle: React.CSSProperties = {
  display: 'block', color: '#4a5568', fontSize: '0.875rem', marginBottom: '0.5rem', fontWeight: 600,
};
const sectionStyle: React.CSSProperties = {
  backgroundColor: '#ffffff', border: '1px solid #eef0f6', borderRadius: '16px',
  padding: '1.5rem', marginBottom: '1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
};

export default function AppCreateListing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const editCommunity = searchParams.get('community');
  const isEditMode = !!editId;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState<Currency>('EUR');
  const [category, setCategory] = useState<string>('Electronics');
  const [listingType, setListingType] = useState<ListingType>('Sell');
  const [condition, setCondition] = useState<ItemCondition>('Good');
  const [location, setLocation] = useState('');
  const [locationCoords, setLocationCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [images, setImages] = useState<string[]>([]);
  const [visibility, setVisibility] = useState<Visibility>('community');
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingEdit, setLoadingEdit] = useState(isEditMode);
  const [userCommunity, setUserCommunity] = useState('');

  useEffect(() => {
    apiGet<{ data: { community?: string; nationality?: string; city?: string } }>('/api_profile', undefined, true)
      .then(res => {
        const d = res.data;
        setUserCommunity(d.community || (d.nationality && d.city ? `${d.nationality} in ${d.city}` : ''));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEditMode) return;
    const params: Record<string, string> = { id: editId! };
    if (editCommunity) params.community = editCommunity;
    apiGet<{ data: any }>('/api_listing_details', params, true)
      .then((res) => {
        const l = res.data;
        setTitle(l.title || '');
        setDescription(l.description || '');
        setPrice(l.price != null ? String(l.price) : '');
        setCurrency((l.currency as Currency) || 'EUR');
        setCategory(l.category || 'Electronics');
        setListingType((l.listingType as ListingType) || 'Sell');
        setCondition((l.condition as ItemCondition) || 'Good');
        setLocation(l.location || '');
        setImages(l.images || []);
        setVisibility((l.type as Visibility) || 'community');
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load listing'))
      .finally(() => setLoadingEdit(false));
  }, [isEditMode, editId, editCommunity]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { setError('Title is required.'); return; }
    if (!description.trim()) { setError('Description is required.'); return; }
    if (!category) { setError('Please select a category.'); return; }
    if (!location.trim()) { setError('Location is required.'); return; }
    if (listingType !== 'Free' && (!price || isNaN(parseFloat(price)) || parseFloat(price) < 0)) {
      setError('Please enter a valid price.'); return;
    }

    setSubmitting(true);
    setError(null);

    const payload: Record<string, unknown> = {
      title: title.trim(),
      description: description.trim(),
      price: listingType === 'Free' ? 0 : parseFloat(price),
      currency,
      category,
      listingType,
      condition,
      location: location.trim(),
      ...(locationCoords ? { latitude: locationCoords.lat, longitude: locationCoords.lng } : {}),
      type: visibility,
      images,
    };

    try {
      if (isEditMode) {
        const params: Record<string, string> = { id: editId! };
        if (editCommunity) params.community = editCommunity;
        await apiPut('/api_update_listing', payload, params);
        navigate(`/marketplace/${slugify(title)}-${editId}?community=${encodeURIComponent(editCommunity || '')}`);
      } else {
        const res = await apiPost<{ data?: { id: string }; id?: string }>('/api_save_listing', payload, true);
        const newId = res.data?.id || res.id;
        navigate(newId ? `/marketplace/${slugify(title)}-${newId}` : '/marketplace');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : `Failed to ${isEditMode ? 'update' : 'create'} listing.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this listing? This cannot be undone.')) return;
    setDeleting(true);
    try {
      const params: Record<string, string> = { id: editId! };
      if (editCommunity) params.community = editCommunity;
      await apiDelete('/api_delete_listing', params);
      navigate('/marketplace');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete listing.');
    } finally {
      setDeleting(false);
    }
  };

  if (loadingEdit) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f7f8fc' }}>
        <AppNav />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 64px)' }}>
          <div style={{ width: '36px', height: '36px', border: '3px solid #f0f0f0', borderTop: '3px solid #FF385C', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f7f8fc' }}>
      <AppNav />

      <div style={{ maxWidth: '640px', margin: '0 auto', padding: '1.75rem 1.25rem 4rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <button onClick={() => navigate(isEditMode ? `/marketplace/${editId}` : '/marketplace')}
            style={{ background: 'none', border: 'none', color: '#6b7a99', fontSize: '0.875rem', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '0.35rem', fontWeight: 500 }}>
            ← {isEditMode ? 'Back to Listing' : 'Marketplace'}
          </button>
          {isEditMode && (
            <button onClick={handleDelete} disabled={deleting}
              style={{ background: 'none', border: '1.5px solid #fca5a5', borderRadius: '8px', color: '#dc2626', fontSize: '0.82rem', cursor: 'pointer', padding: '0.35rem 0.75rem', fontWeight: 600, opacity: deleting ? 0.6 : 1 }}>
              {deleting ? 'Deleting...' : 'Delete Listing'}
            </button>
          )}
        </div>

        <h1 style={{ color: '#1a1f36', fontWeight: 800, fontSize: '1.625rem', margin: '0 0 1.75rem', letterSpacing: '-0.03em' }}>
          {isEditMode ? 'Edit Listing' : 'Post a Listing'}
        </h1>

        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <div style={sectionStyle}>
            <h2 style={{ color: '#1a1f36', fontWeight: 700, fontSize: '0.95rem', margin: '0 0 1.25rem' }}>Basic Info</h2>
            <div style={{ marginBottom: '1.125rem' }}>
              <label style={labelStyle}>Title <span style={{ color: '#ef4444' }}>*</span></label>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. IKEA Dining Table – great condition" maxLength={200} style={inputStyle}
                onFocus={(e) => (e.currentTarget.style.borderColor = '#FF385C')} onBlur={(e) => (e.currentTarget.style.borderColor = '#e2e6f0')} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <label style={{ ...labelStyle, margin: 0 }}>Description <span style={{ color: '#ef4444' }}>*</span></label>
                <AIDescriptionHelper itemType="marketplace" currentText={[title, description].filter(Boolean).join('\n')} onAccept={setDescription} />
              </div>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the item — dimensions, colour, reason for selling, pickup/delivery options..." rows={4} maxLength={2000}
                style={{ ...inputStyle, resize: 'vertical' }} onFocus={(e) => (e.currentTarget.style.borderColor = '#FF385C')} onBlur={(e) => (e.currentTarget.style.borderColor = '#e2e6f0')} />
              <p style={{ color: '#8896ab', fontSize: '0.75rem', margin: '0.25rem 0 0', textAlign: 'right' }}>{description.length}/2000</p>
            </div>
          </div>

          {/* Listing Type */}
          <div style={sectionStyle}>
            <h2 style={{ color: '#1a1f36', fontWeight: 700, fontSize: '0.95rem', margin: '0 0 1.25rem' }}>Listing Type</h2>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {LISTING_TYPES.map((t) => {
                const active = listingType === t;
                return (
                  <button key={t} type="button" onClick={() => setListingType(t)}
                    style={{ flex: 1, padding: '0.75rem', border: active ? '1.5px solid #FF385C' : '1.5px solid #e2e6f0', borderRadius: '10px', backgroundColor: active ? '#fff5eb' : '#f7f8fc', color: active ? '#FF385C' : '#4a5568', fontWeight: active ? 700 : 500, cursor: 'pointer', fontSize: '0.9rem', transition: 'all 0.15s' }}>
                    {t === 'Sell' ? '💰 Sell' : t === 'Rent' ? '🏠 Rent' : '🎁 Free'}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Price */}
          {listingType !== 'Free' && (
            <div style={sectionStyle}>
              <h2 style={{ color: '#1a1f36', fontWeight: 700, fontSize: '0.95rem', margin: '0 0 1.25rem' }}>Price <span style={{ color: '#ef4444' }}>*</span></h2>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <select value={currency} onChange={(e) => setCurrency(e.target.value as Currency)}
                  style={{ ...inputStyle, width: 'auto', flexShrink: 0, paddingRight: '2rem', cursor: 'pointer', appearance: 'auto' }}>
                  {CURRENCIES.map((c) => <option key={c} value={c}>{CURRENCY_SYMBOL[c]} {c}</option>)}
                </select>
                <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" min="0" step="0.01" style={{ ...inputStyle, flex: 1 }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = '#FF385C')} onBlur={(e) => (e.currentTarget.style.borderColor = '#e2e6f0')} />
              </div>
            </div>
          )}

          {/* Category */}
          <div style={sectionStyle}>
            <h2 style={{ color: '#1a1f36', fontWeight: 700, fontSize: '0.95rem', margin: '0 0 1.25rem' }}>Category <span style={{ color: '#ef4444' }}>*</span></h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.5rem' }}>
              {MARKETPLACE_CATEGORIES.map((cat) => {
                const active = category === cat;
                return (
                  <button key={cat} type="button" onClick={() => setCategory(cat)}
                    style={{ padding: '0.625rem 0.5rem', border: active ? '1.5px solid #FF385C' : '1.5px solid #e2e6f0', borderRadius: '10px', backgroundColor: active ? '#fff5eb' : '#f7f8fc', color: active ? '#FF385C' : '#4a5568', fontSize: '0.78rem', fontWeight: active ? 700 : 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem', transition: 'all 0.15s' }}>
                    <span>{MARKETPLACE_EMOJI[cat]}</span><span>{cat}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Condition */}
          <div style={sectionStyle}>
            <h2 style={{ color: '#1a1f36', fontWeight: 700, fontSize: '0.95rem', margin: '0 0 1.25rem' }}>Condition</h2>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              {ITEM_CONDITIONS.map((c) => {
                const active = condition === c;
                return (
                  <button key={c} type="button" onClick={() => setCondition(c)}
                    style={{ padding: '0.5rem 0.875rem', border: active ? '1.5px solid #FF385C' : '1.5px solid #e2e6f0', borderRadius: '20px', backgroundColor: active ? '#fff5eb' : '#f7f8fc', color: active ? '#FF385C' : '#4a5568', fontSize: '0.82rem', fontWeight: active ? 700 : 500, cursor: 'pointer', transition: 'all 0.15s' }}>
                    {c}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Location */}
          <div style={sectionStyle}>
            <LocationInput value={location} onChange={setLocation} onCoordinates={(lat, lng) => setLocationCoords({ lat, lng })} placeholder="e.g. Amsterdam, Netherlands" required inputStyle={inputStyle} />
          </div>

          {/* Visibility */}
          <div style={sectionStyle}>
            <h2 style={{ color: '#1a1f36', fontWeight: 700, fontSize: '0.95rem', margin: '0 0 1.25rem' }}>Visibility</h2>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {([
                { val: 'community', label: '🏘️ Community' },
                { val: 'global',    label: `🌍 Global in ${userCommunity?.split(' in ')[1] || 'your city'}` },
                { val: 'private',   label: '🔒 Private' },
              ] as { val: Visibility; label: string }[]).map(({ val, label }) => {
                const active = visibility === val;
                return (
                  <button key={val} type="button" onClick={() => setVisibility(val)}
                    style={{ flex: '1 1 120px', padding: '0.75rem', border: active ? '1.5px solid #FF385C' : '1.5px solid #e2e6f0', borderRadius: '10px', backgroundColor: active ? '#fff5eb' : '#f7f8fc', color: active ? '#FF385C' : '#4a5568', fontWeight: active ? 700 : 500, cursor: 'pointer', fontSize: '0.85rem', transition: 'all 0.15s' }}>
                    {label}
                  </button>
                );
              })}
            </div>
            <p style={{ color: '#8896ab', fontSize: '0.78rem', margin: '0.75rem 0 0' }}>
              {visibility === 'community'
                ? `Community — Visible to "${userCommunity || 'your community'}"`
                : visibility === 'global'
                  ? `Global in ${userCommunity?.split(' in ')[1] || 'your city'} — Visible to everyone in your city`
                  : 'Private — Members must request to join and be approved.'}
            </p>
          </div>

          {/* Images */}
          <div style={sectionStyle}>
            <h2 style={{ color: '#1a1f36', fontWeight: 700, fontSize: '0.95rem', margin: '0 0 1.25rem' }}>Images <span style={{ color: '#8896ab', fontWeight: 400 }}>(optional, up to 6)</span></h2>
            <ImageUploader value={images} onChange={setImages} maxImages={6} />
          </div>

          {error && (
            <div style={{ padding: '0.875rem 1rem', backgroundColor: '#fff5f5', border: '1px solid #fed7d7', borderRadius: '10px', color: '#c53030', fontSize: '0.875rem', marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          <button type="submit" disabled={submitting}
            style={{ width: '100%', padding: '1rem', backgroundColor: '#FF385C', border: 'none', borderRadius: '12px', color: '#fff', fontWeight: 700, fontSize: '1rem', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.75 : 1, transition: 'all 0.15s', boxShadow: '0 4px 16px rgba(255,56,92,0.3)' }}>
            {submitting ? (isEditMode ? 'Saving...' : 'Posting...') : (isEditMode ? 'Save Changes ✨' : 'Post Listing ✨')}
          </button>
        </form>
      </div>
    </div>
  );
}
